import {
  Component,
  ComponentDidLoad,
  ComponentWillLoad,
  Element,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Map } from 'immutable';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import WogaaLayerHelper from 'wogaa-layer-helper/es5';

import {
  DetectBot,
  FeedbackAttributes,
  RatingAttributes,
  RatingSubmit,
  SubmitFeedback,
  WogaaLayerActions,
  WogaaLayerEventTypes,
} from '@actions/WogaaLayerActions';
import { AppRouter } from '@entities/AppRouter';
import { Behavior, InformationalServiceBehavior } from '@entities/Behavior';
import { Configuration } from '@entities/Configuration';
import { CustomWindow } from '@entities/CustomWindow';
import { Design } from '@entities/Design';
import { FloatingButtonState } from '@entities/FloatingButtonState';
import { Question, QuestionSegment } from '@entities/Question';
import { ServiceType } from '@entities/ServiceType';
import { TargetedDisplay } from '@entities/TargetedDisplay';
import { TransactionalService } from '@entities/TransactionalService';
import { TriggerOn } from '@entities/TriggerOn';
import { BuildNumber, EnvConfigs, Stage, Version } from '@environment/resources';
import { BehaviorService } from '@services/BehaviorService';
import { CoreService } from '@services/CoreService';
import { Logger } from '@services/Logger';
import { OngoingTransactionalService } from '@services/OngoingTransactionalService';
import { QuestionsCacheService } from '@services/QuestionsCacheService';
import SnowplowService from '@services/SnowplowService';
import * as Utils from '@services/UtilsService';
import appStore from '@stores/app.store';
import { analyzeUserBehavior, CheckFailureResponse, getLatestEntries } from 'utils/bot-detection';

declare let window: CustomWindow;

@Component({
  tag: 'wog-sentiments',
  shadow: true,
})
export class Sentiments implements ComponentDidLoad, ComponentWillLoad {
  @Element() element: HTMLElement;
  @Prop() isEnableForIs = false;
  @Prop() platform: TargetedDisplay = TargetedDisplay.Desktop;
  @Prop() reportSuiteId: string;
  @Prop() tsCollection: TransactionalService[];
  @Prop() customize: Configuration;

  @State() transactionalServices = Map<string, TransactionalService>();
  @State() isMobile = false;
  @State() isTablet = false;
  @State() mouseData: Array<{ x: number; y: number; time: number }>;
  @State() keyPressCount = 0;
  @State() isMouseClickCount = 0;
  @State() isTouchCount = 0;
  @State() isBot = false;
  @State() botChecksFailed: CheckFailureResponse[] = [];

  private readonly snowplow = new SnowplowService();
  private readonly questionCache = new QuestionsCacheService();
  private readonly ongoingServices = new OngoingTransactionalService();
  private readonly behaviorService = new BehaviorService();
  private readonly coreService = new CoreService();

  private feedbackCancelTimetout;
  private surveyActionTimetout;

  private maxMouseDataSize = 150;
  private isFull = false;
  private currentIndex = 0;

  constructor() {
    this.mouseData = new Array(this.maxMouseDataSize);
  }

  locationChangeSubscription: Subscription;

  @Watch('platform')
  watchPlatformHandler(newValue: TargetedDisplay) {
    this.isMobile = newValue === TargetedDisplay.Mobile;
    this.isTablet = newValue === TargetedDisplay.Tablet;
  }

  // Override the pushState method of the history API
  private overridePushState() {
    // Define a wrapper function to intercept calls to pushState
    const _wr = (type: string) => {
      // Store a reference to the original pushState method
      const orig = history[type];
      // Return a function that replaces the original pushState method
      return (...args: any[]) => {
        // Call the original pushState method with the provided arguments
        const rv = orig.apply(history, args);
        // Dispatch a custom event indicating that pushState was called
        const e = new Event(type);
        // Attach the arguments passed to pushState to the event
        (e as any).arguments = args;
        // Dispatch the custom event on the window object
        window.dispatchEvent(e);
        // Return the return value of the original pushState method
        return rv;
      };
    };
    // Override the pushState method with the wrapper function
    history.pushState = _wr('pushState');
  }

  handleKeyDown = () => {
    this.keyPressCount++;

    if (this.keyPressCount > 5) {
      this.keyPressCount = 0;
    }
  };

  handleMouseMove = (event: MouseEvent) => {
    const { clientX: x, clientY: y } = event;
    const now: number = performance.now();

    this.mouseData[this.currentIndex] = { x, y, time: now };

    this.currentIndex = (this.currentIndex + 1) % this.maxMouseDataSize;

    if (this.currentIndex === 0) {
      this.isFull = true;
    }
  };

  private handleMouseLeave = () => {
    this.isBot = false;
    this.mouseData = new Array(this.maxMouseDataSize);
    this.currentIndex = 0;
  };

  private handleMouseClick = () => {
    this.isMouseClickCount++;
  };

  private handleTouchStart = () => {
    this.isTouchCount++;
  };

  componentWillLoad() {
    this.overridePushState();

    // Listen to URL changes
    // This defined how Sentiments state change works for
    // single page applications.
    this.locationChangeSubscription = merge(
      fromEvent(window, 'locationchange'),
      fromEvent(window, 'hashchange'),
      fromEvent(window, 'pushState'),
    )
      .pipe(
        debounceTime(200),
        // filter(_ => !appStore.get('isTransactionalServiceActive')), // If TS popup is active do nothing //disabled this code to reset the widget when navigated
        distinctUntilChanged(),
      )
      .subscribe(() => {
        // appStore.set('floatingButtonState', FloatingButtonState.Initial); //disabled this code to stop hiding the widget when navigated
        const config = appStore.get('configuration');
        this.ongoingServices.forceRemoveValues();
        const numOfServices = this.ongoingServices.getNumberOfServices();

        // regenerate uuid for every new rating occur
        const sentimentsId = uuidv4();
        this.coreService.setSentimentsId(sentimentsId);
        Logger.log('new sentiments id generated', { sessionId: sentimentsId });

        if (numOfServices > 0) {
          this.ongoingServices.refreshActiveTrackingId();
          const activeTrackingId = this.ongoingServices.getActiveTrackingId();
          const tsInfo = this.transactionalServices.get(activeTrackingId);
          const isInSession = this.behaviorService.isServiceCompleted(tsInfo.trackingId);
          const tsService = this.ongoingServices.getService(tsInfo.trackingId);
          const routerState = Utils.getStateForTransactionalService(tsInfo, config, {
            platform: this.platform,
            isInSession: isInSession,
            isRatingSubmitted: tsService.rs,
            isCompleted: tsService.ic,
          });

          appStore.set('serviceType', ServiceType.TransactionalService);
          appStore.set('route', routerState);
          return;
        }

        const routerState = Utils.getStateForInformationalService(
          this.isEnableForIs,
          this.behaviorService.isServiceCompleted('app'),
          config,
        );
        appStore.set('serviceType', ServiceType.InformationalService);
        appStore.set('route', routerState);
        return;
      });

    this.isMobile = this.platform === TargetedDisplay.Mobile;
    const configs = this.customize;
    const options = {
      ...appStore.get('configuration'),
      ...configs,
    };
    // options.questions = appStore.get('configuration').questions;
    appStore.set('configuration', {
      ...options,
      defaultState:
        options.design === Design.SmileyFace
          ? AppRouter.FloatingActionButtonRoute
          : AppRouter.TabbedWidgetRoute,
    });
    Logger.log('sentiments configurations load complete', {
      configs: appStore.get('configuration'),
    });

    appStore.set('reportSuiteId', this.reportSuiteId);
    const sentimentsId = uuidv4();
    this.coreService.setSentimentsId(sentimentsId);
    Logger.log('new sentiments id generated', { sessionId: sentimentsId });
    this.transactionalServices = Utils.transactionalServicesResolve(this.tsCollection);

    Logger.log('transactional services collection resolve complete', {
      transactionalServices: this.transactionalServices.toJSON(),
    });

    // Questions merge with new transactional services
    this.questionCache.sync(this.reportSuiteId, this.ongoingServices.getOngoingServiceIds());
    Logger.log('loading transactional service queue complete');
    const numOfActiveServices = this.ongoingServices.getNumberOfServices();
    const config = appStore.get('configuration');
    if (numOfActiveServices === 0) {
      Logger.log('no Transactional Service(s) found in session');
      Logger.log('sentiments is enabled for Informational Service mode', {
        isEnableForIs: this.isEnableForIs,
      });
      this.ongoingServices.removeActiveTrackingId();
      const sentimentsState = Utils.getStateForInformationalService(
        this.isEnableForIs,
        this.behaviorService.isServiceCompleted('app'),
        config,
      );
      appStore.set('serviceType', ServiceType.InformationalService);
      appStore.set('route', sentimentsState);
    } else {
      // TS state
      // Refresh active token is required to since if the deletion of
      // old tracking id could be still in session
      this.ongoingServices.refreshActiveTrackingId();
      let activeTrackingId = this.ongoingServices.getActiveTrackingId();

      // Set stack level transactional service
      if (!activeTrackingId && this.ongoingServices.getNumberOfServices() > 0) {
        const activeService = this.ongoingServices.getService(activeTrackingId);
        activeTrackingId = activeService.id;
        this.ongoingServices.setActiveTrackingId(activeTrackingId);
      }

      Logger.log('Getting ts info', {
        activeTrackingId: activeTrackingId,
        transactionalServices: this.transactionalServices,
      });

      const tsInfo = this.transactionalServices.get(activeTrackingId);
      const isInSession = this.behaviorService.isServiceCompleted(tsInfo.trackingId);
      const tsService = this.ongoingServices.getService(tsInfo.trackingId);
      const routerState = Utils.getStateForTransactionalService(tsInfo, config, {
        platform: this.platform,
        isInSession: isInSession,
        isRatingSubmitted: tsService.rs,
        isCompleted: tsService.ic,
      });
      const { abQuestionTesting, transactionalServiceType } = tsInfo;

      appStore.set('transactionalServiceType', transactionalServiceType);
      appStore.set('tsAbQuestionTesting', abQuestionTesting);
      appStore.set('serviceType', ServiceType.TransactionalService);
      appStore.set('route', routerState);
    }
  }

  componentDidLoad() {
    try {
      Logger.log('sentiments did load');

      Utils.setSentimentsInfo(this.element, Version, BuildNumber, Stage, this.showRatingPopup);
      this.element.addEventListener('mousemove', this.handleMouseMove);
      this.element.addEventListener('keydown', this.handleKeyDown);
      this.element.addEventListener('input', this.handleKeyDown);
      this.element.addEventListener('mouseleave', this.handleMouseLeave);
      this.element.addEventListener('click', this.handleMouseClick);
      this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      this.wogaaLayerSubscribe();
    } catch (e) {
      Logger.log('Sentiments error while component-did-load', { err: e });
      console.error('Sentiments error while component-did-load', e);
    }
  }

  disconnectedCallback() {
    Utils.unsubscribeAll(this.locationChangeSubscription);
    this.element.removeEventListener('mousemove', this.handleMouseMove);
    this.element.removeEventListener('keydown', this.handleKeyDown);
    this.element.removeEventListener('input', this.handleKeyDown);
    this.element.removeEventListener('mouseleave', this.handleMouseLeave);
    this.element.removeEventListener('click', this.handleMouseClick);
    this.element.removeEventListener('touchstart', this.handleTouchStart);
    clearTimeout(this.feedbackCancelTimetout);
    clearTimeout(this.surveyActionTimetout);
  }

  @Listen('tabButtonClick')
  handTabButtonClick() {
    this.handleFloatingButtonClick();
  }

  @Listen('displayComplete')
  handleDisplayComplete() {
    appStore.set('floatingButtonState', FloatingButtonState.ThankYouCollapse);
    appStore.set('isTransactionalServiceActive', false);
    // Handle display for IS and TS
    const serviceType = appStore.get('serviceType');
    const activeTrackingId = this.ongoingServices.getActiveTrackingId();
    const appState = this.getSentimentState(serviceType, activeTrackingId);
    appStore.set('route', appState);
    this.clearSessionVariables(serviceType);
    this.coreService.setCollapsedMode(true);
  }

  @Listen('feedbackCancel')
  handleFeedbackCancel() {
    appStore.set('floatingButtonState', FloatingButtonState.FeedbackCollapse);
    const appState = this.getSentimentState(
      appStore.get('serviceType'),
      this.ongoingServices.getActiveTrackingId(),
    );
    appStore.set('route', appState);
    appStore.set('floatingButtonStateTimeout', false);
    appStore.set('isTransactionalServiceActive', false);
    appStore.set('submittedSentimentsId', this.coreService.getSentimentsId());
    const sentimentsId = uuidv4();
    this.coreService.setSentimentsId(sentimentsId);
    this.coreService.setCollapsedMode(true);
    Logger.log('new new sentiments id generated', { sessionId: sentimentsId });
    this.clearSessionVariables(appStore.get('serviceType'));
    appStore.set('isFeedbackSubmitted', false);

    const config = appStore.get('configuration');
    let ctsgSurveyEnabled = false;
    const serviceType = appStore.get('serviceType');
    if (serviceType === ServiceType.TransactionalService) {
      const activeTrackingId = this.ongoingServices.getActiveTrackingId();
      ctsgSurveyEnabled = this.getCTSGSurveyConfig(serviceType, activeTrackingId).isEnabled;
    } else {
      ctsgSurveyEnabled = config.ctsgSurveyConfig.isEnabled;
    }

    if (ctsgSurveyEnabled) {
      appStore.set('route', AppRouter.SurveyRoute);
    } else {
      this.feedbackCancelTimetout = setTimeout(() => {
        appStore.set('floatingButtonStateTimeout', true);
      }, 600);
    }
  }

  @Listen('surveyActionComplete')
  handleSurveyActionComplete() {
    appStore.set('floatingButtonState', FloatingButtonState.SurveyCollapse);
    appStore.set('isTransactionalServiceActive', false);
    // Handle display for IS and TS
    const serviceType = appStore.get('serviceType');
    const activeTrackingId = this.ongoingServices.getActiveTrackingId();
    const appState = this.getSentimentState(serviceType, activeTrackingId);
    appStore.set('route', appState);
    this.clearSessionVariables(serviceType);
    this.coreService.setCollapsedMode(true);

    const isFeedbackSubmitted = appStore.get('isFeedbackSubmitted');
    if (isFeedbackSubmitted) {
      this.surveyActionTimetout = setTimeout(() => {
        appStore.set('floatingButtonStateTimeout', true);
      }, 600);
    }
  }

  @Listen('feedbackSubmit')
  handleFeedbackSubmit(
    event: CustomEvent<{
      values: FeedbackAttributes[];
    }>,
  ) {
    const serviceType = appStore.get('serviceType');
    let sentimentsId = this.coreService.getSentimentsId();
    const trackingId = this.ongoingServices.getActiveTrackingId();
    let extraOpts = {};

    if (serviceType === ServiceType.TransactionalService) {
      extraOpts = {
        trackingId,
      };
    }

    for (const item of event.detail.values) {
      const value = {
        ...item,
        sentimentsId,
        ...extraOpts,
        rsid: appStore.get('reportSuiteId'),
        questionConfigId: this.getQuestionConfigId(serviceType, trackingId),
      };
      this.snowplow.dispatch(new SubmitFeedback(value));
    }
    appStore.set('submittedSentimentsId', sentimentsId);
    const newSentimentsId = uuidv4();
    this.coreService.setSentimentsId(newSentimentsId);
    Logger.log('new new sentiments id generated', { sessionId: sentimentsId });

    appStore.set('isFeedbackSubmitted', true);

    const config = appStore.get('configuration');
    let ctsgSurveyEnabled = false;
    if (serviceType === ServiceType.TransactionalService) {
      ctsgSurveyEnabled = this.getCTSGSurveyConfig(serviceType, trackingId).isEnabled;
    } else {
      ctsgSurveyEnabled = config.ctsgSurveyConfig.isEnabled;
    }

    if (ctsgSurveyEnabled) {
      appStore.set('route', AppRouter.SurveyRoute);
    } else {
      appStore.set('route', AppRouter.ThankYouSectionRoute);
    }
  }

  @Listen('ratingClose')
  handleRatingClose() {
    appStore.set('floatingButtonState', FloatingButtonState.RatingCollapse);
    const serviceType = appStore.get('serviceType');
    this.coreService.setCollapsedMode(true);
    this.clearSessionVariables(serviceType);
    const appState = this.getSentimentState(
      serviceType,
      this.ongoingServices.getActiveTrackingId(),
    );
    appStore.set('isTransactionalServiceActive', false);
    appStore.set('route', appState);

    this.mouseData = [];
    this.keyPressCount = 0;
    this.isTouchCount = 0;
    this.isBot = false;
  }

  @Listen('ratingSubmit')
  handleRatingSubmit(event: CustomEvent<{ value: number }>) {
    const serviceType = appStore.get('serviceType');
    const sentimentsId = this.coreService.getSentimentsId();
    const reportSuiteId = appStore.get('reportSuiteId');

    let options: RatingAttributes = {
      sentimentsId,
      rsid: reportSuiteId,
      rating: event.detail.value,
      questionConfigId: this.customize.questionConfigId,
      ratingScaleDesign: this.customize.ratingScaleDesign,
    };
    if (serviceType === ServiceType.TransactionalService) {
      const activeTrackingId = this.ongoingServices.getActiveTrackingId();
      const tsConfig = this.transactionalServices.get(activeTrackingId);

      // TODO Handle this gracefully
      if (!tsConfig) {
        // no tsConfig found throw error
        throw new Error('no ts config found');
      }

      options = {
        ...options,
        trackingId: activeTrackingId,
        sentimentsId: sentimentsId,
        questionConfigId: this.getQuestionConfigId(serviceType, activeTrackingId),
      };

      if (tsConfig.behavior === Behavior.NeverAgainInSession) {
        if (!this.behaviorService.isServiceCompleted(activeTrackingId)) {
          this.behaviorService.newService(activeTrackingId);
        }
      }
      // Update the status of the ongoing service
      this.ongoingServices.updateIsRatingSubmitted(activeTrackingId, true);
    } else if (serviceType === ServiceType.InformationalService) {
      const config = appStore.get('configuration');
      if (config.behavior === InformationalServiceBehavior.NeverAgainInSession) {
        const service = 'app';
        if (!this.behaviorService.isServiceCompleted(service)) {
          this.behaviorService.newService(service);
        }
      }
    }

    appStore.set('latestRating', event.detail.value);
    this.snowplow.dispatch(new RatingSubmit(options));
  }

  @Listen('ratingSubmitAnimationComplete')
  handleRatingSubmitAnimationComplete() {
    if (appStore.get('serviceType') === ServiceType.TransactionalService) {
      const trackingId: string = this.ongoingServices.getActiveTrackingId();
      if (trackingId && trackingId !== '') {
        if (this.questionCache.has(trackingId)) {
          const route = this.getPlatformSpecificRoute(this.platform);
          appStore.set('route', route);
        } else {
          appStore.set('route', AppRouter.ConnectionErrorRoute);
        }
      } else {
        console.error('no valid tracking id found');
        Logger.log('no valid tracking id found', { trackingId });
        appStore.set('route', AppRouter.ConnectionErrorRoute);
      }
    } else {
      const route = this.getPlatformSpecificRoute(this.platform);
      appStore.set('route', route);
    }
  }

  @Listen('wizardComplete')
  handleWizardComplete() {
    appStore.set('submittedSentimentsId', this.coreService.getSentimentsId());
    const sentimentsId = uuidv4();
    this.coreService.setSentimentsId(sentimentsId);
    Logger.log('new new sentiments id generated', { sessionId: sentimentsId });

    appStore.set('isFeedbackSubmitted', true);
    const config = appStore.get('configuration');
    if (config.ctsgSurveyConfig) {
      if (config.ctsgSurveyConfig.isEnabled) {
        appStore.set('route', AppRouter.SurveyRoute);
      } else {
        appStore.set('route', AppRouter.ThankYouSectionRoute);
      }
    } else {
      appStore.set('route', AppRouter.ThankYouSectionRoute);
    }
  }

  @Listen('questionComplete')
  handleWizardQuestionComplete(
    event: CustomEvent<{
      values: {
        questionNumber: number;
        questionId: string;
        questionType: number;
        response: string;
      }[];
    }>,
  ) {
    const serviceType = appStore.get('serviceType');
    const sentimentsId = this.coreService.getSentimentsId();
    const trackingId = this.ongoingServices.getActiveTrackingId();
    for (const item of event.detail.values) {
      const value = {
        ...item,
        ...(serviceType === ServiceType.TransactionalService && { trackingId }),
        rsid: appStore.get('reportSuiteId'),
        sentimentsId,
        questionConfigId: this.getQuestionConfigId(serviceType, trackingId),
      };
      this.snowplow.dispatch(new SubmitFeedback(value));
    }
  }

  private handleFetchQuestions = (event: CustomEvent<{ trackingId: string }>) => {
    this.questionCache
      .fetchIfNotExist(this.reportSuiteId, event.detail.trackingId)
      .then(questions => {
        if (questions.has(event.detail.trackingId)) {
          const route = this.getPlatformSpecificRoute(this.platform);
          appStore.set('route', route);
        }
      })
      .catch(err => {
        Logger.log('sentiments config fetch error', err);
      });
  };

  /**
   * Handle Floating button click
   */
  private handleFloatingButtonClick = () => {
    appStore.set('route', AppRouter.SatisfactionRatingRoute);
  };

  /**
   * Handle Floating button keyboard enter or spacebar
   */
  private handleFloatingButtonKeyEnter = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      appStore.set('route', AppRouter.SatisfactionRatingRoute);
    }
  };

  /**
   * Get Platform specific route state to laod
   * @param platform
   */
  getPlatformSpecificRoute(platform: TargetedDisplay): AppRouter {
    return TargetedDisplay.Mobile === platform
      ? AppRouter.QuestionSectionRoute
      : AppRouter.FeedbackSectionRoute;
  }

  getSentimentState(serviceType: ServiceType, trackingId: string | undefined): AppRouter {
    const configuration = appStore.get('configuration');
    if (serviceType === ServiceType.InformationalService) {
      return Utils.getStateForInformationalService(
        this.isEnableForIs,
        this.behaviorService.isServiceCompleted('app'),
        configuration,
      );
    }

    // Service Type Transactional Service
    if (trackingId === undefined) {
      return AppRouter.DisplayNoneRoute;
    }
    const transactionalService = this.transactionalServices.get(trackingId);
    const isInSession = this.behaviorService.isServiceCompleted(trackingId);
    const ongoingService = this.ongoingServices.getService(trackingId);
    return Utils.getStateForTransactionalService(transactionalService, configuration, {
      isInSession,
      platform: this.platform,
      isRatingSubmitted: ongoingService.rs,
      isCompleted: ongoingService.ic,
    });
  }

  private clearSessionVariables(serviceType: ServiceType) {
    if (serviceType === ServiceType.TransactionalService) {
      if (this.ongoingServices.getNumberOfServices() === 0) {
        appStore.set('serviceType', ServiceType.InformationalService);
      }
      Logger.log('removed tracking id from session');
    }
  }

  /**
   * Complete Transactional Service
   * @param tsOpts Transactional service
   */
  private completeTransactionalService(tsOpts: TransactionalService): void {
    const isTransactionalServiceActive = appStore.get('isTransactionalServiceActive');
    const sessionConfig = this.ongoingServices.getService(tsOpts.trackingId);

    if (tsOpts && tsOpts.isEnabled && tsOpts.targetedDisplays.includes(this.platform)) {
      if (tsOpts.transactionalServiceType) {
        appStore.set('transactionalServiceType', tsOpts.transactionalServiceType);
      }

      if (tsOpts.abQuestionTesting) {
        appStore.set('tsAbQuestionTesting', tsOpts.abQuestionTesting);
      }

      if (!(!isTransactionalServiceActive && 'triggerOn' in tsOpts)) {
        return;
      }

      if (tsOpts.behavior === Behavior.NeverAgainInTransaction && sessionConfig.rs) {
        appStore.set('route', AppRouter.DisplayNoneRoute);
        appStore.set('isTransactionalServiceActive', false);
        return;
      }

      if (tsOpts.triggerOn === TriggerOn.EndOfTransaction) {
        if (this.behaviorService.isServiceCompleted(tsOpts.trackingId)) {
          appStore.set('route', AppRouter.DisplayNoneRoute);
          appStore.set('isTransactionalServiceActive', false);
          return;
        }

        appStore.set('isTransactionalServiceActive', true);
        appStore.set('route', AppRouter.SatisfactionRatingRoute);
        return;
      }

      if (tsOpts.triggerOn === TriggerOn.UserTriggered) {
        if (this.behaviorService.isServiceCompleted(tsOpts.trackingId)) {
          appStore.set('route', AppRouter.DisplayNoneRoute);
          appStore.set('isTransactionalServiceActive', false);
          return;
        }

        const defaultRoute = appStore.get('configuration').defaultState;
        appStore.set('route', defaultRoute);
        appStore.set('isTransactionalServiceActive', true);
        return;
      }
    }
  }

  private getQuestions(
    serviceType: ServiceType,
    trackingId: string | null,
    isQuestions: Question[],
  ): Question[] | null {
    if (serviceType === ServiceType.TransactionalService) {
      if (trackingId && trackingId !== '' && this.questionCache.has(trackingId)) {
        return this.questionCache.get(trackingId).questions;
      }

      return null;
    }
    return isQuestions;
  }

  private getQuestionConfig(serviceType: ServiceType, trackingId: string) {
    if (serviceType === ServiceType.InformationalService) {
      return null;
    }

    if (this.questionCache.has(trackingId)) {
      return this.questionCache.get(trackingId);
    }

    return null;
  }

  private getCTSGSurveyConfig(serviceType: ServiceType, trackingId: string) {
    if (serviceType === ServiceType.InformationalService) {
      return null;
    }

    if (this.transactionalServices.has(trackingId)) {
      return this.transactionalServices.get(trackingId).ctsgSurveyConfig;
    }

    return null;
  }

  private getQuestionConfigId(serviceType: ServiceType, trackingId: string) {
    const questionConfig = this.getQuestionConfig(serviceType, trackingId);
    return questionConfig !== null ? questionConfig.id : this.customize.questionConfigId;
  }

  /**
   * Render sentiments State
   * @param sentimentsState sentiments state
   * @param configuration app configuration
   */
  private renderSentimentsState(sentimentsState: AppRouter, configuration: Configuration) {
    const {
      appearance,
      questions,
      themeColor,
      thankYouMessageBad,
      thankYouMessageGood,
      design,
      position,
      ctsgSurveyConfig,
    } = configuration;
    switch (sentimentsState) {
      case AppRouter.FloatingActionButtonRoute: {
        return (
          <wog-floating-action-button
            isMobile={this.isMobile}
            position={configuration.position}
            widgetLabel={configuration.widgetLabel}
            onClick={this.handleFloatingButtonClick}
            onKeyDown={this.handleFloatingButtonKeyEnter}
          />
        );
      }
      case AppRouter.TabbedWidgetRoute: {
        const switchCollapsedMode = this.isMobile && this.coreService.getCollapsedMode();
        return (
          <wog-tabbed-widget
            collapsed={switchCollapsedMode}
            widgetLabel={configuration.widgetLabel}
          />
        );
      }
      case AppRouter.SatisfactionRatingRoute: {
        return <wog-rating />;
      }
      case AppRouter.ConnectionErrorRoute: {
        const trackingId = this.ongoingServices.getActiveTrackingId();
        return (
          <wog-connection-error
            trackingId={trackingId}
            position={position}
            appearance={appearance}
            design={design}
            themeColor={themeColor}
            onConnectWindowClose={() => this.handleRatingClose()}
            onFetchData={this.handleFetchQuestions}
          />
        );
      }
      case AppRouter.FeedbackSectionRoute: {
        // only desktop
        const segment =
          appStore.get('latestRating') <= 4
            ? QuestionSegment.BadQuestion
            : QuestionSegment.GoodQuestion;

        // it should have a questionConfig if it is a transactionalService type
        const questionConfig = this.getQuestionConfig(
          appStore.get('serviceType'),
          this.ongoingServices.getActiveTrackingId(),
        );

        let _questions = questionConfig?.questions;

        if (!questionConfig) {
          // it is a preference service if doesnt have questionConfig
          _questions = questions;
        }

        return (
          <wog-modern-feedback
            position={position}
            appearance={appearance}
            design={design}
            themeColor={themeColor}
            questions={_questions}
            segment={segment}
          />
        );
      }
      case AppRouter.QuestionSectionRoute: {
        // only mobile
        const segment =
          appStore.get('latestRating') <= 4
            ? QuestionSegment.BadQuestion
            : QuestionSegment.GoodQuestion;
        const _questions = this.getQuestions(
          appStore.get('serviceType'),
          this.ongoingServices.getActiveTrackingId(),
          questions,
        );
        return (
          <wog-question-section
            appearance={appearance}
            design={design}
            themeColor={themeColor}
            questions={_questions}
            segment={segment}
          />
        );
      }
      case AppRouter.ThankYouSectionRoute: {
        const lastValue = appStore.get('latestRating');
        if (appStore.get('serviceType') === ServiceType.TransactionalService) {
          const activeTrackingId = this.ongoingServices.getActiveTrackingId();
          if (
            activeTrackingId &&
            activeTrackingId !== '' &&
            this.questionCache.has(activeTrackingId)
          ) {
            const questionConfig = this.questionCache.get(activeTrackingId);
            questionConfig.thankYouMessageBad;
            return (
              <wog-thank-you
                ratedValue={lastValue}
                thankYouMessageBad={questionConfig.thankYouMessageBad}
                thankYouMessageGood={questionConfig.thankYouMessageGood}
              />
            );
          }
        }
        return (
          <wog-thank-you
            ratedValue={lastValue}
            thankYouMessageBad={thankYouMessageBad}
            thankYouMessageGood={thankYouMessageGood}
          />
        );
      }
      case AppRouter.SurveyRoute: {
        const isFeedbackSubmitted = appStore.get('isFeedbackSubmitted');
        const submittedSentimentsId = appStore.get('submittedSentimentsId');
        let surveyIds;
        const serviceType = appStore.get('serviceType');
        if (serviceType === ServiceType.TransactionalService) {
          const activeTrackingId = this.ongoingServices.getActiveTrackingId();
          surveyIds = this.getCTSGSurveyConfig(serviceType, activeTrackingId).surveyIds;
        } else {
          surveyIds = ctsgSurveyConfig.surveyIds;
        }

        return (
          <wog-survey-popup
            position={position}
            appearance={appearance}
            design={design}
            themeColor={themeColor}
            surveyIds={surveyIds}
            isFeedbackSubmitted={isFeedbackSubmitted}
            sentimentsId={submittedSentimentsId}
          />
        );
      }
      default:
        return;
    }
  }

  private checkIsBot() {
    const isMobileDevice = !!(this.isMobile || this.isTablet);
    const mouseData = getLatestEntries(
      this.mouseData,
      this.isFull,
      this.maxMouseDataSize,
      this.currentIndex,
      100,
    );

    const { isBot, botChecksFailed } = analyzeUserBehavior(
      this.isMouseClickCount,
      mouseData,
      this.keyPressCount,
      isMobileDevice,
      this.isTouchCount,
    );

    return { isBot, content: { botChecksFailed } };
  }

  /**
   * WOGAA layer helper subscriber
   */
  private wogaaLayerSubscribe() {
    return new WogaaLayerHelper(window.wogaaLayer, (_context, action: WogaaLayerActions) => {
      if (action && 'type' in action) {
        Logger.log('processing wogaa-layer action', { action });

        switch (action.type) {
          case WogaaLayerEventTypes.StartTS: {
            if (!Utils.checkPropertyValidity(action.payload, ['trackingId'])) {
              Logger.log('invalid payload for start-transactional-service', { action });
              break;
            }

            this.ongoingServices.forceRemoveValues(); //garbage collector to clear already completed records

            const { trackingId } = action.payload;
            const { transactionalServiceType } = this.transactionalServices.get(trackingId);

            appStore.set('transactionalServiceType', transactionalServiceType);

            if (this.ongoingServices.isServiceStarted(trackingId)) {
              Logger.log('tracking id already started', { action });
              break;
            }

            // manually set transactional service to inactive when previous transactional service is completed
            // and need to start another new transactional service
            const isServiceStarted = this.ongoingServices.isServiceStarted(trackingId);
            const isTransactionalServiceActive = appStore.get('isTransactionalServiceActive');
            if (!isServiceStarted && isTransactionalServiceActive) {
              appStore.set('isTransactionalServiceActive', false);
            }

            Logger.log('requesting sentiments to fetch ts questions from the server if needed');
            this.questionCache.fetchIfNotExist(this.reportSuiteId, trackingId);

            Logger.log('executing a valid start transaction', { trackingId });
            const service = {
              id: trackingId,
              time: new Date().getTime(),
              sId: uuidv4(),
            };
            this.ongoingServices.addService(service);

            // No need to change the state
            if (appStore.get('isTransactionalServiceActive')) {
              break;
            }

            this.ongoingServices.setActiveTrackingId(trackingId);
            // appStore.set('activeService', service);
            const tsOpts = this.transactionalServices.get(trackingId);
            const isInSession = this.behaviorService.isServiceCompleted(tsOpts.trackingId);
            const tsService = this.ongoingServices.getService(trackingId);
            const routerState = Utils.getStateForTransactionalService(
              tsOpts,
              appStore.get('configuration'),
              {
                platform: this.platform,
                isInSession: isInSession,
                isRatingSubmitted: tsService.rs,
                isCompleted: tsService.ic,
              },
            );

            appStore.set('serviceType', ServiceType.TransactionalService);
            // Set the latest started transactional service behaviour
            appStore.set('route', routerState);
            Logger.log('start transaction execution complete', { action });
            break;
          }
          case WogaaLayerEventTypes.CompleteTS: {
            if (!Utils.checkPropertyValidity(action.payload, ['trackingId'])) {
              Logger.log('invalid payload for complete-transactional-service', { action });
              break;
            }
            const { trackingId } = action.payload;

            const isTransactionalServiceActive = appStore.get('isTransactionalServiceActive');
            if (
              window.wogaaCustom.siteInfo.txnSvcs.includes(trackingId) &&
              this.transactionalServices.has(trackingId) &&
              this.ongoingServices.isServiceStarted(trackingId) &&
              !isTransactionalServiceActive
            ) {
              Logger.log('executing a valid complete transaction', { trackingId });
              this.ongoingServices.setActiveTrackingId(trackingId);
              const tsOpts = this.transactionalServices.get(trackingId);
              this.completeTransactionalService(tsOpts);
            }

            this.ongoingServices.updateIsCompleted(trackingId, true);
            Logger.log('complete transaction execution complete', { action });
            break;
          }
          case WogaaLayerEventTypes.OpenInformationalService: {
            // This is a special case for GBP
            // This will open Informational Service layer
            Logger.log('forcefully opening sentiments');
            this.handleFloatingButtonClick();
            break;
          }
          case WogaaLayerEventTypes.RatingSubmit: {
            Logger.log('dispatching event submit rating', { action });

            if (Utils.checkPropertyValidity(action.payload, ['rating'])) {
              this.snowplow.submitRatingEvent(action.payload);
            }

            const { isBot, content } = this.checkIsBot();
            if (isBot) {
              const value = {
                label: 'rating',
                value: Number(isBot),
                content,
                ...action.payload,
              };

              this.snowplow.dispatch(new DetectBot(value));
            }

            break;
          }
          case WogaaLayerEventTypes.SubmitFeedback: {
            Logger.log('dispatching event submit feedback', { action });

            this.snowplow.submitFeedbackEvent(action.payload);

            const { isBot, content } = this.checkIsBot();
            if (isBot) {
              const value = {
                label: 'feedback',
                value: Number(isBot),
                content,
                ...action.payload,
              };

              this.snowplow.dispatch(new DetectBot(value));
            }

            break;
          }
          case WogaaLayerEventTypes.SetUserId: {
            Logger.log('setting snowplow user id for `wsm` tracker', { action });
            this.snowplow.setUserId(action.payload.id);
            break;
          }
          case WogaaLayerEventTypes.DetectBot: {
            this.snowplow.submitBotDetectEvent(action.payload);
            break;
          }
        }
      }
    });
  }

  private showRatingPopup() {
    appStore.set('route', AppRouter.SatisfactionRatingRoute);
  }

  render() {
    const currentRoute = appStore.get('route');
    const configuration = appStore.get('configuration');
    return (
      <div id="wog--sentiments-content">
        <link href={EnvConfigs.StylesheetURL} rel="stylesheet" />
        {this.renderSentimentsState(currentRoute, configuration)}
      </div>
    );
  }
}
